import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wrench } from 'lucide-react';
import { Card, CardContent, Button } from '@scio3/ui';
import { getWriteToolDisplayName, formatToolArgs } from '../utils/tool-display';

interface ApprovalCardProps {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  onApprove: (toolCallId: string) => void;
  onDeny: (toolCallId: string) => void;
}

/**
 * Inline approval card rendered when a write tool needs user confirmation.
 * Shows the tool action name, a brief summary of arguments, and
 * Approve / Deny buttons that send the result back via addToolResult.
 */
export function ApprovalCard({ toolCallId, toolName, args, onApprove, onDeny }: ApprovalCardProps) {
  const { t } = useTranslation();
  const [responded, setResponded] = useState(false);

  const displayName = t(getWriteToolDisplayName(toolName));
  const argsSummary = formatToolArgs(toolName, args, t);

  return (
    <Card className="my-2 border-primary/20">
      <CardContent className="p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <Wrench className="h-4 w-4 text-primary" />
          <span>{displayName}</span>
        </div>

        {argsSummary && <p className="mb-3 text-xs text-muted-foreground">{argsSummary}</p>}

        <p className="mb-3 text-xs text-muted-foreground">{t('optibot.actionPending')}</p>

        <div className="flex gap-2">
          <Button
            size="sm"
            variant="default"
            disabled={responded}
            onClick={() => {
              setResponded(true);
              onApprove(toolCallId);
            }}
          >
            {t('optibot.approve')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={responded}
            onClick={() => {
              setResponded(true);
              onDeny(toolCallId);
            }}
          >
            {t('optibot.deny')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
